import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  ensureHeaderSettings,
  ensureHighlightedFeaturesSectionSettings,
  ensurePrimayCTASettings,
  ensureSecondaryCTASettings,
  ensureTrimCardSettings,
} from './utils'
import type { IPage, IProductSettings } from '@/types'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import {useJams, useJazelAppConfig} from "@jazel/jazel-vuetify";

export const useProductSettingsStore = defineStore('productSettings', () => {
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const initialized = ref(false)

  const saving = ref(false)
  const savingError = ref<Error | null>(null)

  const appConfig = useJazelAppConfig()
  const accountSettings = useAccountSettingsStore()
  const jamsService = useJams()
  const settings = ref<IProductSettings | null>(null)

  const updateSettings = (newSettings: IProductSettings) => {
    settings.value = { ...newSettings }
  }

  const saveSettings = async (newSettings: any) => {
    saving.value = true
    savingError.value = null

    const params = {
      accountId: accountSettings.accountId,
      endpoint: appConfig.jamsBackendUrl,
      isClientUser: accountSettings.isClientUser,
      settings: newSettings,
      productId: appConfig.productId,
      token: accountSettings.sessionToken,
    }
    const savingResult = await jamsService.setProductSettings(params)

    if (!savingResult) {
      error.value = new Error(`Error saving product settings`)
    } else {
      updateSettings(newSettings)
      saving.value = false
    }

    saving.value = false
  }

  const setProductSettings = (settings: any | null) => {
    if (settings.pages === null) {
      settings.pages = []
    }

    const normalizedPages = ensurePageSettingsDefaults(settings?.pages ?? [])
    const normalizedGlobalSettings = ensureGlobalSettingsDefaults(settings?.globalSettings ?? {})

    updateSettings({
      pages: normalizedPages,
      globalSettings: normalizedGlobalSettings,
    })
  }

  const ensurePageSettingsDefaults = (pages: any[]) => {
    return pages.map((page: IPage) => {
      const newPage = { ...page }

      newPage.highlightedFeatures = ensureHighlightedFeaturesSectionSettings(
        newPage.highlightedFeatures,
      )

      newPage.primaryCTA = ensurePrimayCTASettings(newPage.primaryCTA, accountSettings)
      newPage.secondaryCTA = ensureSecondaryCTASettings(newPage.secondaryCTA, accountSettings)
      newPage.secondaryCTA = ensureSecondaryCTASettings(newPage.secondaryCTA, accountSettings)
      newPage.overrideGlobalPrimaryCTA = newPage.overrideGlobalPrimaryCTA ?? false
      newPage.overrideGlobalSecondaryCTA = newPage.overrideGlobalSecondaryCTA ?? false

      return newPage
    })
  }

  const ensureGlobalSettingsDefaults = (settings: any) => {
    const globalSettings = { ...settings }

    globalSettings.header = ensureHeaderSettings(globalSettings.header)
    globalSettings.primaryCTA = ensurePrimayCTASettings(globalSettings.primaryCTA, globalSettings)
    globalSettings.secondaryCTA = ensureSecondaryCTASettings(
      globalSettings.secondaryCTA,
      globalSettings,
    )
    globalSettings.trimCard = ensureTrimCardSettings(globalSettings.trimCard)

    return globalSettings
  }

  return {
    // State
    loading,
    error,
    initialized,
    settings,
    saving,
    savingError,

    // Actions
    updateSettings,
    saveSettings,
    setProductSettings,
  }
})
